import React, { Component, Fragment } from 'react';
import withStyles from '@mui/styles/withStyles';
import Grid from '../../../../component/form/Grid';
import PrimaryButton, { ModalPrimaryButton, OutlineButton, SaveButton } from '../../../../component/form/PrimaryButton';
import {
    InlineField,
    InlineFieldRightAlignChildren,
    InlineHeaderRightAlignChildren
} from '../../../../component/form/Inline';
import ModalStepTitle from '../../../../component/form/ModalStepTitle';
import LineItemAutoComplete from '../../../../component/form/LineItemAutoComplete';
import ModalStepSubHeader from '../../../../component/form/ModalStepSubHeader';
import Select from '../../../../component/form/Select';
import TextField from '../../../../component/form/TextField';
import SaveIcon from '../../../../component/icon/SaveIcon';
import CloseIcon from '../../../../component/icon/CloseIcon';
import NextIcon from '../../../../component/icon/NextIcon';
import { DialogActions, IconButton } from '@mui/material';
import { GST, price_amendment_range } from '../../../../util/products';
import Table, { DraggableTableRow, TableCell } from '../../../../component/form/Table';
import { calculateTotals, INVOICE_STATUS_OPTIONS } from './InvoiceModalConstants';
import BackIcon from '../../../../component/icon/BackIcon';
import UsdCircleIcon from '../../../../component/icon/UsdCircleIcon';
import { joinDefined, prettyPrice } from '../../../../util/strings';
import { isNullOrUndefined, limitRange, round } from '../../../../util/objects';
import Checkbox from '../../../../component/form/Checkbox';
import { isRelatedObjectUndefined } from '../../../../util/graphql';
import { lookupItemSource } from '../../../quotes/quotes-modal/QuotesModal';
import GatedComponent from '../../../../component/GatedComponent';
import TickIcon from '../../../../component/icon/TickIcon';
import { DeleteIcon, EditIcon } from '../../../../component/IconIndex';
import Icon from '@mui/material/Icon';
import { DragDropContext, Draggable, Droppable } from '../../../../component/DragDrop';
import InputAdornment from '@mui/material/InputAdornment';
import Hidden from '../../../../component/Hidden';

class InvoiceDetailsTab extends Component {
    state = {
        loaded: false,
        showPrefill: false,
        unitValues: [],
        includeBilled: false,
        includeQuoted: false,
        includeBooked: false
    };

    static getDerivedStateFromProps = ({ prefill, invoice }, { loaded }) => {
        if (!loaded && !!invoice) {
            let showPrefill = false;
            if (!!prefill) {
                const prefillQuoted = !!prefill.quotedLineItems && prefill.quotedLineItems.length > 0;
                const prefillBilled = !!prefill.billedLineItems && prefill.billedLineItems.length > 0;
                const prefillBooked = !!prefill.bookedLineItems && prefill.bookedLineItems.length > 0;
                showPrefill = !!prefillQuoted || !!prefillBilled || !!prefillBooked;
            }
            return {
                unitValues: (invoice.InvoiceItems || []).map(x => calculateUnitValue(x, invoice.IsPurchase)),
                showPrefill,
                loaded: true
            };
        }
        return null;
    };

    render() {
        const {
            classes,
            nextTab,
            previousTab,
            onCancel,
            loading,
            onSubmit,
            onSubmitToXero,
            canEdit,
            canSubmit,
            stepNumber,
            hasExportedToXero,
            onAuthoriseToXero,
            invoice,
            form
        } = this.props;
        const isDirty = form.getDirty().length;
        const typeTitle = invoice.IsPurchase ? 'Bill' : 'Invoice';
        let title;
        if (stepNumber === 'Two') {
            title = 'Update the line items and other details';
        } else {
            title = `#${invoice.ID}`;
            if (invoice.LegacyKey) {
                title += ` (${invoice.LegacyKey})`;
            }
            if (stepNumber === 'Three') {
                title = invoice.ID ? `Submit ${typeTitle}` : `Preview the new ${typeTitle}`;
            }
        }

        const lockedByXero = ['PAID', 'AUTHORISED', 'DELETED', 'VOIDED'].indexOf(invoice.XeroStatus) > -1;

        return (
            <Fragment>
                <Grid container spacing={2} className={classes.root}>
                    <ModalStepTitle number={stepNumber} title={title} header={typeTitle} />
                    <Grid item xs={12} className={classes.modalContent}>
                        <Grid container>
                            {canEdit ? this.renderInvoiceDetails() : this.renderReadOnlyInvoiceDetails()}
                            <GatedComponent isEnabledCode={'ADMIN'}>
                                {canSetAnyPrice => this.renderInvoiceItems(canSetAnyPrice)}
                            </GatedComponent>
                        </Grid>
                        <br />
                        {this.renderSummary()}
                    </Grid>
                </Grid>
                <DialogActions className={classes.modalFooter}>
                    <OutlineButton onClick={loading ? undefined : onCancel} color="primary" disabled={loading}>
                        <CloseIcon />
                        <Hidden mdDown>Close</Hidden>
                    </OutlineButton>

                    {canEdit || canSubmit ? (
                        <PrimaryButton onClick={previousTab} disabled={loading} className={classes.back}>
                            <BackIcon />
                            <Hidden mdDown>Back</Hidden>
                        </PrimaryButton>
                    ) : null}

                    <InlineFieldRightAlignChildren lineHeight={'normal'}>
                        <span />

                        {canEdit ? (
                            <SaveButton disabled={loading || !isDirty} onClick={() => onSubmit()}>
                                <SaveIcon />
                                <span className={classes.svgLabel}>Save</span>
                            </SaveButton>
                        ) : null}

                        {canSubmit ? (
                            <ModalPrimaryButton
                                disabled={loading || !(!!invoice && !!invoice.ID && invoice.ID !== '0')}
                                onClick={() => onSubmitToXero()}
                            >
                                <UsdCircleIcon />
                                <span className={classes.svgLabel}>Submit {typeTitle}</span>
                            </ModalPrimaryButton>
                        ) : null}

                        {hasExportedToXero && !lockedByXero ? (
                            <GatedComponent showComponentCode={'FM_ACCESS_Invoicing'}>
                                {() => {
                                    return (
                                        <ModalPrimaryButton disabled={loading} onClick={() => onAuthoriseToXero()}>
                                            <UsdCircleIcon />
                                            <span className={classes.svgLabel}>Authorise {typeTitle}</span>
                                        </ModalPrimaryButton>
                                    );
                                }}
                            </GatedComponent>
                        ) : null}

                        {!canEdit && !canSubmit ? (
                            <PrimaryButton onClick={nextTab} disabled={loading}>
                                <Hidden mdDown>{hasExportedToXero ? 'View PDF' : 'Payments'}</Hidden>
                                <NextIcon />
                            </PrimaryButton>
                        ) : null}

                        {canEdit ? (
                            <PrimaryButton onClick={nextTab} disabled={loading || !!isDirty}>
                                <Hidden mdDown>Next Step</Hidden>
                                <NextIcon />
                            </PrimaryButton>
                        ) : null}
                    </InlineFieldRightAlignChildren>
                </DialogActions>
            </Fragment>
        );
    }

    renderInvoiceDetails() {
        const { invoice, canEdit, hasExportedToXero } = this.props;

        return (
            <Grid bucket>
                <Grid item xs={12} md={3}>
                    <TextField
                        label={`${invoice.IsPurchase ? 'Bill' : 'Invoice'} Dated`}
                        type="date"
                        value={invoice.InvoiceDate}
                        onChange={e => this.onChangeInvoiceProperty('InvoiceDate', e.target.value)}
                        disabled={!canEdit || !!invoice.InvoiceSent}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <Select
                        label="Payment Method"
                        options={INVOICE_STATUS_OPTIONS}
                        value={invoice.InvoiceStatus || 'Normal'}
                        onChange={e =>
                            this.onChangeInvoiceProperty(
                                'InvoiceStatus',
                                e.target.value === 'Normal' ? null : e.target.value
                            )
                        }
                        allowNone={false}
                        //disabled={!canEdit}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField
                        label="Due Date"
                        type="date"
                        value={invoice.InvoiceDueDate}
                        onChange={e => this.onChangeInvoiceProperty('InvoiceDueDate', e.target.value)}
                        disabled={!canEdit}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <TextField
                        label="Status"
                        value={hasExportedToXero ? invoice.XeroStatus : 'NOT EXPORTED'}
                        disabled={true}
                    />
                </Grid>
            </Grid>
        );
    }

    renderReadOnlyInvoiceDetails() {
        const { invoice, classes, hasExportedToXero } = this.props;
        if (!invoice.Customer) invoice.Customer = { CustomerName: '' };
        let splitName = invoice.Customer.FirstName || invoice.Customer.Surname;
        let customerName = splitName
            ? joinDefined([invoice.Customer.FirstName, invoice.Customer.MiddleName, invoice.Customer.Surname], ' ')
            : invoice.Customer.CustomerName;

        const addressMapping = [
            invoice.Customer.AddressLine1,
            invoice.Customer.AddressLine2,
            invoice.Customer.Suburb,
            invoice.Customer.State,
            invoice.Customer.Postcode,
            invoice.Customer.Country
        ];

        let result = addressMapping.reduce((string, val) => {
            let space = string.length ? ' ' : '';
            return string + (val ? space + val : '');
        }, '');
        return (
            <Grid item xs={12}>
                <InlineField>
                    <table className={classes.detailsTable}>
                        <thead>
                            <tr>
                                <th>Addressed To</th>
                                <th>{`${invoice.IsPurchase ? 'Bill' : 'Invoice'} Dated`}</th>
                                <th>Payment Method</th>
                                <th>Due Date</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {customerName}
                                    <br />
                                    {result}
                                </td>
                                <td>{invoice.InvoiceDate}</td>
                                <td>
                                    {invoice.InvoiceStatus
                                        ? INVOICE_STATUS_OPTIONS.find(e => e.value === invoice.InvoiceStatus).label
                                        : 'Normal'}
                                </td>
                                <td>{invoice.InvoiceDueDate}</td>
                                <td>{hasExportedToXero ? invoice.XeroStatus : 'NOT EXPORTED'}</td>
                            </tr>
                        </tbody>
                    </table>
                </InlineField>
                <br />
            </Grid>
        );
    }

    renderInvoiceItems(canSetAnyPrice) {
        const { classes, invoice, canEdit, onEdit, loading, hasExportedToXero, prefill } = this.props;
        const lockedByXero = ['PAID', 'AUTHORISED', 'DELETED', 'VOIDED'].indexOf(invoice.XeroStatus) > -1;
        const prefillItems = [];
        const { showPrefill } = this.state;
        if (prefill && !!showPrefill) {
            prefill.quotedLineItems.length > 0 &&
                prefillItems.push({
                    label: `Approved Quote`,
                    value: 'includeQuoted'
                });

            prefill.billedLineItems.length > 0 &&
                prefillItems.push({
                    label: `Bills Raised`,
                    value: 'includeBilled'
                });

            prefill.bookedLineItems.length > 0 &&
                prefillItems.push({
                    label: `Orders Placed`,
                    value: 'includeBooked'
                });
        }

        const tableColumns = [
            '',
            'Item Code/SKU',
            'Description of product or service',
            'Price',
            'Quantity',
            'Prepaid',
            'GST',
            'Line Total',
            'Source',
            ''
        ];

        if (!!invoice.IsPurchase) {
            tableColumns.splice(5, 1); // prepaid
        }

        if (!canEdit) {
            tableColumns.shift(); // drag
            tableColumns.pop(); // source
            tableColumns.pop(); // delete
        }

        return (
            <Fragment>
                {!!canEdit ? (
                    <Grid bucket>
                        <Grid item xs={12} md={6} lg={5}>
                            {(!!showPrefill && prefillItems.length > 0 && (
                                <InlineHeaderRightAlignChildren header={'Source Line Items from:'}>
                                    <div
                                        style={{
                                            flex: '1 1 100%',
                                            display: 'flex',
                                            gap: '6px 12px',
                                            marginBottom: -10
                                        }}
                                    >
                                        {prefillItems.map((item, i) => (
                                            <Checkbox
                                                key={i}
                                                checked={!!this.state[item.value]}
                                                label={item.label}
                                                value={'' + item.value}
                                                onChange={e => this.onChooseSource(e.target)}
                                            />
                                        ))}
                                    </div>
                                </InlineHeaderRightAlignChildren>
                            )) || (
                                <ModalStepSubHeader
                                    header={`Review ${invoice.IsPurchase ? 'Bill' : 'Invoice'} Line Items`}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={6} lg={7}>
                            <LineItemAutoComplete
                                isPurchase={invoice.IsPurchase}
                                variations
                                disabled={!canEdit}
                                onSelect={item => this.onAddNewLineItem(item, invoice.IsPurchase)}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <div style={{ width: '100%', paddingTop: '1rem' }}>
                        {hasExportedToXero && (
                            <div className={classes.floatRight}>
                                <OutlineButton disabled={lockedByXero ? true : loading} onClick={() => onEdit()}>
                                    <EditIcon />
                                    Edit {invoice.IsPurchase ? 'Bill' : 'Invoice'}
                                </OutlineButton>
                            </div>
                        )}
                        <ModalStepSubHeader header={`${invoice.IsPurchase ? 'Bill' : 'Invoice'} Line Items`} />
                    </div>
                )}

                <InlineField lineHeight={'normal'}>
                    <Grid item xs={12}>
                        <DragDropContext onDragEnd={this.onDragEnd}>
                            <Table
                                columns={tableColumns}
                                tbody={
                                    <Droppable id="droppable">
                                        {provided => (
                                            <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                                {invoice.InvoiceItems.map((row, i) =>
                                                    this.renderInvoiceItem(row, i, canSetAnyPrice)
                                                )}
                                                {provided.placeholder}
                                            </tbody>
                                        )}
                                    </Droppable>
                                }
                            />
                        </DragDropContext>
                    </Grid>
                </InlineField>
            </Fragment>
        );
    }

    renderInvoiceItem(row, i, canSetAnyPrice) {
        const { classes, invoice, canEdit } = this.props;
        const { unitValues } = this.state;
        const unitValue = unitValues[i];
        const lineAmount = row.Prepaid ? 0 : unitValue * row.Quantity;
        const isBill = !!invoice.IsPurchase;
        const gstAmount = hasGst(row, isBill) ? prettyPrice(lineAmount / ((GST + 1) * 10)) : 'FREE';
        const product = {
            ID: row.Product && row.Product.ID ? row.Product.ID : 0,
            GST: row.Product && row.Product.ID > 0 ? (isBill ? row.Product.ITC : row.Product.GST) : 0,
            Title: row.Product && row.Product.ID > 0 ? row.Product.Title : '',
            InternalItemID: row.Product && row.Product.ID > 0 ? row.Product.InternalItemID : ''
        };
        const variant = {
            ID: row.Variation && row.Variation.ID ? row.Variation.ID : 0,
            GST: row.Variation && row.Variation.ID > 0 ? (isBill ? row.Variation.ITC : row.Variation.GST) : 0,
            ShortDescription: row.Variation && row.Variation.ID > 0 ? row.Variation.ShortDescription : '',
            InternalItemID: row.Variation && row.Variation.ID > 0 ? row.Variation.InternalItemID : ''
        };

        const title =
            row.Title ||
            product.Title +
                (!!Number(variant.ID)
                    ? ' | ' + (!!variant.ShortDescription ? variant.ShortDescription : variant.InternalItemID)
                    : '');
        const code = row.ItemCode || (!!Number(variant.ID) ? variant.InternalItemID : product.InternalItemID);
        const key = row.ID || row.sortID || 'new-' + i;
        return (
            <Draggable key={key} id={key} isDragDisabled={!canEdit} index={i}>
                {provided => (
                    <DraggableTableRow
                        pad
                        ref={provided.innerRef}
                        draggableProps={provided.draggableProps}
                        className={canEdit ? classes.removableRow : null}
                    >
                        {!!canEdit && (
                            <TableCell dataLabel={'Sorted as #' + (i + 1)} tiny>
                                <span {...provided.dragHandleProps} title="Reorder" tabIndex="0">
                                    <IconButton
                                        className={classes.iconButton}
                                        style={{ pointerEvents: 'none' }}
                                        tabIndex="-1"
                                        size="large"
                                    >
                                        <Icon>drag_indicator</Icon>
                                    </IconButton>
                                </span>
                            </TableCell>
                        )}
                        <TableCell dataLabel="Item Code/SKU">{code}</TableCell>
                        <TableCell dataLabel="Description">{title}</TableCell>
                        <TableCell dataLabel="Price" className={classes.rightCell}>
                            {canEdit ? (
                                <TextField
                                    className={classes.priceField}
                                    type="number"
                                    onChange={e => this.onChangeUnitPrice(i, e.target.value)}
                                    onBlur={e => this.onBlurUnitPrice(i, e.target.value, canSetAnyPrice)}
                                    value={unitValue}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                />
                            ) : (
                                <span>{prettyPrice(unitValue || 0)}</span>
                            )}
                        </TableCell>
                        <TableCell dataLabel="Quantity" className={classes.rightCell}>
                            {!!canEdit ? (
                                <TextField
                                    className={classes.quantityField}
                                    type="number"
                                    inputProps={{ min: '0' }}
                                    onChange={e => this.onChangeQuantity(i, e.target.value)}
                                    value={row.Quantity || '0'}
                                />
                            ) : (
                                <span>{Number(row.Quantity || '0').toFixed(0)}</span>
                            )}
                        </TableCell>
                        {!isBill && (
                            <TableCell dataLabel="Prepaid" tiny className={classes.centreCell}>
                                {!!canEdit ? (
                                    <Checkbox
                                        onChange={e => this.onChangePrepaid(i, e.target.checked)}
                                        checked={!!row.Prepaid || false}
                                    />
                                ) : row.Prepaid ? (
                                    <TickIcon color={'primary'} />
                                ) : (
                                    <CloseIcon color={'disabled'} />
                                )}
                            </TableCell>
                        )}
                        <TableCell dataLabel="GST" className={classes.rightCell}>
                            {gstAmount}
                        </TableCell>
                        <TableCell dataLabel="Line Total" className={classes.rightCell}>
                            <b>{prettyPrice(lineAmount)}</b>
                        </TableCell>
                        {!!canEdit && (
                            <TableCell dataLabel="Source">
                                {!!row.quoteID
                                    ? 'Approved Quote'
                                    : !!row.billID
                                    ? 'Bills Raised'
                                    : !!row.orderID
                                    ? 'Orders Placed'
                                    : !!row.ID
                                    ? 'Saved'
                                    : 'Added'}
                            </TableCell>
                        )}
                        {!!canEdit && (
                            <TableCell dataLabel="Delete" tiny>
                                <IconButton
                                    className={classes.iconButton}
                                    title={'Delete'}
                                    onClick={() => this.onDeleteLineItem(i)}
                                    size="large"
                                >
                                    <DeleteIcon className={classes.icon} />
                                </IconButton>
                            </TableCell>
                        )}
                    </DraggableTableRow>
                )}
            </Draggable>
        );
    }

    renderSummary() {
        const { classes, invoice, canEdit } = this.props;
        const totals = calculateTotals(invoice);
        return (
            <>
                <Hidden mdDown>
                    <InlineField>
                        {this.displayReferences()}
                        {this.calculateAmountPaid(totals, canEdit, classes)}
                    </InlineField>
                </Hidden>
                <Hidden mdUp>
                    <>
                        <InlineField>{this.calculateAmountPaid(totals, canEdit, classes)}</InlineField>
                        <br />
                        {this.displayReferences()}
                    </>
                </Hidden>
            </>
        );
    }

    displayReferences() {
        const { classes, invoice } = this.props;
        return (
            <Grid item xs={12} md={9}>
                {invoice.ID && (
                    <table className={classes.detailsTable}>
                        <thead>
                            <tr>
                                <th>Created</th>
                                <th>Xero Reference</th>
                                <th>{!invoice.IsPurchase && 'Sent to Customer'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {invoice.Created}
                                    {' by '}
                                    {invoice.CreatedBy
                                        ? joinDefined([invoice.CreatedBy.FirstName, invoice.CreatedBy.Surname], ' ')
                                        : 'system'}
                                </td>
                                <td>{invoice.XeroReference}</td>
                                <td>{!invoice.IsPurchase && (invoice.InvoiceSent ? 'Yes' : 'No')}</td>
                            </tr>
                        </tbody>
                    </table>
                )}
            </Grid>
        );
    }

    onChangeInvoiceProperty(property, value) {
        const { invoice } = this.props;
        invoice[property] = value;
        // altered props, need to refresh
        this.forceUpdate();
    }

    onChangePrepaid(lineItemIndex, booleanValue) {
        const { invoice } = this.props;
        const lineItem = invoice.InvoiceItems[lineItemIndex];
        lineItem.Prepaid = booleanValue || false;
        // altered props, need to refresh
        this.forceUpdate();
    }

    onChangeQuantity(lineItemIndex, stringValue) {
        const value = typeof stringValue === 'number' ? stringValue : parseInt(stringValue, 10);
        if (isNaN(value) || value < 0) return 0;

        const { unitValues } = this.state;
        const { invoice } = this.props;
        const lineItem = invoice.InvoiceItems[lineItemIndex];
        lineItem.Quantity = value;
        lineItem.Price = calculateBaseValue(lineItem, unitValues[lineItemIndex], invoice.IsPurchase);
        // altered props, need to refresh
        this.forceUpdate();
    }

    onChangeUnitPrice(lineItemIndex, stringValue) {
        const value = typeof stringValue === 'number' ? stringValue : parseFloat(stringValue);
        if (isNaN(value) && '' !== stringValue) return; // allow empty but reject non-numbers

        const { unitValues } = this.state;
        const { invoice } = this.props;
        const lineItem = invoice.InvoiceItems[lineItemIndex];
        unitValues[lineItemIndex] = stringValue;
        lineItem.Price = calculateBaseValue(lineItem, value || 0, invoice.IsPurchase);
        this.setState({ unitValues });
    }

    onBlurUnitPrice(lineItemIndex, stringValue, canSetAnyPrice = false) {
        const value = typeof stringValue === 'number' ? stringValue : parseFloat(stringValue);
        if (isNaN(value)) return;

        const { unitValues } = this.state;
        const { invoice } = this.props;
        const lineItem = invoice.InvoiceItems[lineItemIndex];
        // ensure we have an acceptable price and reformat it nicely
        const valueProp = invoice.IsPurchase ? 'Worth' : 'Price';
        const baseValue =
            lineItem.Variation && lineItem.Variation.ID > 0
                ? lineItem.Variation[valueProp]
                : lineItem.Product['Base' + valueProp];
        const hasGST =
            lineItem.Variation && lineItem.Variation.ID > 0
                ? lineItem.Variation[invoice.IsPurchase ? 'ITC' : 'GST']
                : lineItem.Product[invoice.IsPurchase ? 'ITC' : 'GST'];
        const originalValue = hasGST ? round(baseValue * (GST + 1)) : baseValue;
        const limitedValue = !!canSetAnyPrice
            ? value || 0
            : limitRange(value || 0, originalValue, price_amendment_range, originalValue < 0);

        unitValues[lineItemIndex] = round(limitedValue).toFixed(2);
        lineItem.Price = calculateBaseValue(lineItem, limitedValue, invoice.IsPurchase);
        this.setState({ unitValues });
    }

    onDeleteLineItem(index) {
        const { invoice } = this.props;
        invoice.InvoiceItems.splice(index, 1);

        const { unitValues } = this.state;
        unitValues.splice(index, 1);
        this.setState({ unitValues });
    }

    onAddNewLineItem(item, isPurchase) {
        const { invoice } = this.props;
        const {
            Funeral: { LegacyKey }
        } = invoice;
        const source = lookupItemSource(item, LegacyKey, isPurchase);
        const lineItem = {
            ItemCode: item.ItemCode,
            XeroAccount: item.XeroAccount,
            Quantity: 1,
            Price: source.Cost,
            HasGST: source.GST,
            Prepaid: false,
            Product: {
                ...item.Product
            },
            Variation: {
                ...item.Variation
            },
            Sort: invoice.InvoiceItems.length,
            sortID: generateTempID()
        };

        invoice.InvoiceItems.push(lineItem);

        const { unitValues } = this.state;
        unitValues.push(calculateUnitValue(lineItem, invoice.IsPurchase));
        this.setState({ unitValues });
    }

    onChooseSource(target) {
        const { prefill, invoice } = this.props;
        setInvoiceFromPrefill(target, { prefill, invoice });
        const updateToggle = target.value;
        this.setState({
            [updateToggle]: !!target.checked,
            unitValues: invoice.InvoiceItems.map(x => calculateUnitValue(x, invoice.IsPurchase))
        });
    }

    onDragEnd = ({ operation }) => {
        const { source, target } = operation;
        // dropped outside the list
        if (!target) return;
        const reorder = (list, startIndex, endIndex) => {
            const result = Array.from(list);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result.map((item, idx) => ({ ...item, Sort: idx }));
        };

        const oldIndex = source.sortable?.initialIndex ?? source.index;
        const newIndex = source.sortable?.index ?? target.index;
        const { invoice } = this.props;
        invoice.InvoiceItems = reorder(invoice.InvoiceItems, oldIndex, newIndex);
        this.setState({
            unitValues: invoice.InvoiceItems.map(x => calculateUnitValue(x, invoice.IsPurchase))
        });
    };

    calculateAmountPaid(totals, canEdit, classes) {
        let totalPaid = totals.amountPaid;
        let amountDue = totals.amountDue;
        return (
            <Grid item xs={12} md={3}>
                <table className={classes.costSummaryTable}>
                    <tbody>
                        <tr>
                            <td>
                                <span>Subtotal</span>
                            </td>
                            <td>
                                <span>{prettyPrice(totals.total)}</span>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span>Total GST ({GST * 100}%)</span>
                            </td>
                            <td>
                                <span>{prettyPrice(totals.gst)}</span>
                            </td>
                        </tr>
                        {(!canEdit && (
                            <Fragment>
                                <tr className={classes.totalRow}>
                                    <td>
                                        <b>TOTAL</b>
                                    </td>
                                    <td>
                                        <b>{prettyPrice(totals.totalWithGst)}</b>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <span>Amount Paid</span>
                                    </td>
                                    <td>
                                        <span>{prettyPrice(totalPaid)}</span>
                                    </td>
                                </tr>

                                <tr className={classes.amountDueRow}>
                                    <td>
                                        <span>AMOUNT DUE</span>
                                    </td>
                                    <td>
                                        <span>{prettyPrice(amountDue)}</span>
                                    </td>
                                </tr>
                            </Fragment>
                        )) || (
                            <tr className={classes.totalRow}>
                                <td>
                                    <b>TOTAL</b>
                                </td>
                                <td>
                                    <b>{prettyPrice(totals.totalWithGst)}</b>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Grid>
        );
    }
}

const calculateBaseValue = (invoiceLineItem, newUnitValue, isPurchase) => {
    const unitValue = Number(newUnitValue);
    return round(hasGst(invoiceLineItem, isPurchase) ? unitValue / (GST + 1) : unitValue, 4);
};

const calculateUnitValue = (invoiceLineItem, isPurchase) => {
    const baseValue = Number(invoiceLineItem.Price);
    return round(hasGst(invoiceLineItem, isPurchase) ? baseValue * (GST + 1) : baseValue).toFixed(2);
};

const hasGst = (invoiceLineItem, isPurchase) => {
    return isNullOrUndefined(invoiceLineItem.HasGST)
        ? !isRelatedObjectUndefined(invoiceLineItem.Variation)
            ? invoiceLineItem.Variation[isPurchase ? 'ITC' : 'GST']
            : !isRelatedObjectUndefined(invoiceLineItem.Product) && invoiceLineItem.Product[isPurchase ? 'ITC' : 'GST']
        : invoiceLineItem.HasGST;
};

const setInvoiceFromPrefill = ({ value, checked }, { prefill, invoice }) => {
    const newItems = { list: [] };
    const oldList = invoice.InvoiceItems.filter(e => !!e); // clone
    if (value !== false) {
        const coffin = !!prefill.bookedLineItems
            ? prefill.bookedLineItems.map(y => ({ ...y, orderID: true })).flat()
            : [];
        const bill = !!prefill.billedLineItems ? prefill.billedLineItems.map(y => ({ ...y, billID: true })).flat() : [];
        const quote = !!prefill.quotedLineItems
            ? prefill.quotedLineItems.map(x => x.items.map(y => ({ ...y, quoteID: true }))).flat()
            : [];
        if ('includeBilled' === value) {
            if (bill) {
                //strip the bill items
                newItems.list = oldList.filter(e => !e.billID);
                // add back in if wanted
                if (checked) newItems.list = [].concat(newItems.list, bill);
            }
        } else if ('includeQuoted' === value) {
            if (quote) {
                //strip the quote items
                newItems.list = oldList.filter(e => !e.quoteID);
                // add back in if wanted
                if (checked) newItems.list = [].concat(newItems.list, quote);
            }
        } else if ('includeBooked' === value) {
            //strip the order items
            newItems.list = oldList.filter(e => !e.orderID);
            // add back in if wanted
            if (checked) newItems.list = [].concat(newItems.list, coffin);
        }
    }
    invoice.InvoiceItems = newItems.list.map((e, i) => ({
        ...e,
        Sort: i,
        sortID: e.sortID || generateTempID()
    }));
};

const generateTempID = () => {
    return (
        Date.now().toString(36) +
        Math.random()
            .toString(36)
            .substring(2)
    );
};

const styles = ({ breakpoints }) => ({
    root: {
        margin: '0',
        padding: '50px 30px 30px 30px',
        height: 'auto',
        alignContent: 'start',
        flexDirection: 'column',
        justifyContent: 'space-between',
        display: 'flex',
        width: '100%',
        [breakpoints.down('sm')]: {
            padding: 8
        }
    },

    centreCell: {
        textAlign: 'center',
        '&>div': {
            margin: 0,
            maxWidth: 200,
            '&>*': { textAlign: 'initial' }
        }
    },

    rightCell: {
        textAlign: 'right'
    },

    quoteTemplateColumn: {
        width: '140px'
    },

    selectedColumn: {
        width: '70px'
    },

    detailsTable: {
        width: '100%',
        '& th': {
            textAlign: 'left',
            verticalAlign: 'top',
            fontWeight: '500',
            background: 'white',
            lineHeight: 1
        },
        '& td': {
            textAlign: 'left',
            verticalAlign: 'top',
            lineHeight: 1
        }
    },

    costSummaryTable: {
        borderSpacing: 0,
        borderCollapse: 'separate',
        width: '100%',
        fontSize: '12px',
        '& tr > :first-child': {
            textAlign: 'right',
            padding: '0 20px 0 0'
        },
        '& tr > :not(:first-child)': {
            textAlign: 'right'
        }
    },

    totalRow: {
        '& td': {
            borderWidth: '1px 0',
            borderStyle: 'solid',
            borderColor: '#000'
        }
    },

    amountDueRow: {
        '& td': {
            borderWidth: '0 0 1px 0',
            borderStyle: 'solid',
            borderColor: '#000',
            fontSize: '18px'
        }
    },

    removableRow: {
        backgroundColor: '#ccc'
    },

    iconButton: {
        width: '18px;',
        height: '18px;'
    },

    icon: {
        width: '14px;',
        height: '14px;'
    },

    quantityField: {
        width: 66,
        margin: 0
    },

    priceField: {
        width: 123,
        margin: 0
    },

    submitToXeroButton: {
        height: '15px'
    },

    floatRight: {
        float: 'right'
    },

    back: {
        float: 'left'
    }
});

export default withStyles(styles)(InvoiceDetailsTab);
